import type { RouterOutputs } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { escapeCsvValue } from '../../lib/csv';
import { downloadCsv } from '../../lib/download';

export type MappingDocumentData = NonNullable<
  RouterOutputs['unitsOfCompetency']['generationMappingDocument']
>;

export function groupByProperty<T, K>(
  items: T[],
  getKey: (item: T) => K,
): Array<{ key: K; items: T[] }> {
  const groups: Array<{ key: K; items: T[] }> = [];
  let current: { key: K; items: T[] } | null = null;

  for (const item of items) {
    const key = getKey(item);

    if (current == null || current.key !== key) {
      current = { key, items: [] };
      groups.push(current);
    }

    current.items.push(item);
  }

  return groups;
}

function buildMappingDocumentCsv(data: MappingDocumentData): string {
  const { assessmentTaskDefinitions: tasks } = data;
  const lines: string[] = [];

  lines.push(`Unit Code,${escapeCsvValue(data.unitCode)}`);
  lines.push(`Unit Title,${escapeCsvValue(data.unitTitle)}`);
  lines.push('');

  lines.push('Performance Criteria');
  lines.push(`Code,Description,${escapeCsvValue(tasks.task1)},${escapeCsvValue(tasks.task2)}`);

  for (const pc of data.performanceCriteria) {
    lines.push(
      [
        escapeCsvValue(pc.code),
        escapeCsvValue(pc.description),
        escapeCsvValue(pc.assessmentTask1.join(', ')),
        escapeCsvValue(pc.assessmentTask2.join(', ')),
      ].join(','),
    );
  }

  lines.push('');
  lines.push('Foundation Skills');
  lines.push(`Category,Description,${escapeCsvValue(tasks.task1)},${escapeCsvValue(tasks.task2)}`);

  for (const fs of data.foundationSkills) {
    lines.push(
      [
        escapeCsvValue(fs.category),
        escapeCsvValue(fs.description),
        escapeCsvValue(fs.assessmentTask1.join(', ')),
        escapeCsvValue(fs.assessmentTask2.join(', ')),
      ].join(','),
    );
  }

  lines.push('');
  lines.push('Performance Evidence');
  lines.push(`Code,Description,${escapeCsvValue(tasks.task1)},${escapeCsvValue(tasks.task2)}`);

  for (const pe of data.performanceEvidence) {
    lines.push(
      [
        escapeCsvValue(pe.tag),
        escapeCsvValue(pe.description),
        escapeCsvValue(pe.assessmentTask1.join(', ')),
        escapeCsvValue(pe.assessmentTask2.join(', ')),
      ].join(','),
    );
  }

  lines.push('');
  lines.push('Knowledge Evidence');
  lines.push(`Code,Description,${escapeCsvValue(tasks.task1)},${escapeCsvValue(tasks.task2)}`);

  for (const ke of data.knowledgeEvidence) {
    lines.push(
      [
        escapeCsvValue(ke.tag),
        escapeCsvValue(ke.description),
        escapeCsvValue(ke.assessmentTask1.join(', ')),
        escapeCsvValue(ke.assessmentTask2.join(', ')),
      ].join(','),
    );
  }

  lines.push('');
  lines.push('Assessment Conditions');
  lines.push(escapeCsvValue(data.assessmentConditions));

  return lines.join('\r\n');
}

export function downloadMappingDocumentCsv(data: MappingDocumentData) {
  downloadCsv(buildMappingDocumentCsv(data), `${data.unitCode}-mapping-document.csv`);
}
